import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../App";
import Header from "../components/Header";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const ResetPassword = () => {
  const { API } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleReset = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("Las contrasenas no coinciden");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("La contrasena debe tener al menos 6 caracteres");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: newPassword });
      setSuccess(true);
      toast.success("Contrasena actualizada exitosamente");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al restablecer contrasena");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-black">
        <Header />
        <div className="min-h-screen flex items-center justify-center px-4 pt-24">
          <Card className="w-full max-w-md bg-card/50 backdrop-blur-md border-white/10">
            <CardContent className="pt-6 text-center">
              <p className="text-white/60">Enlace invalido. Solicita un nuevo enlace desde la pagina de inicio de sesion.</p>
              <Button onClick={() => navigate("/auth")} className="mt-4 bg-[#E50914] hover:bg-[#E50914]/90 text-white" data-testid="go-login-btn">
                Ir a iniciar sesion
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <Header />
      <div className="min-h-screen flex items-center justify-center px-4 pt-24 pb-12">
        <Card className="w-full max-w-md bg-card/50 backdrop-blur-md border-white/10">
          <CardHeader>
            <CardTitle className="text-2xl font-bold text-white text-center">
              <img src="/logo-v4.png" alt="CONTAFLIX" className="h-9 w-auto mx-auto" />
            </CardTitle>
            <CardDescription className="text-center text-white/60">
              {success ? "Contrasena actualizada" : "Crea tu nueva contrasena"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {success ? (
              <div className="text-center space-y-4">
                <div className="w-16 h-16 mx-auto bg-green-500/10 rounded-full flex items-center justify-center">
                  <CheckCircle2 className="w-8 h-8 text-green-500" />
                </div>
                <p className="text-white">Tu contrasena ha sido actualizada correctamente.</p>
                <Button
                  onClick={() => navigate("/auth")}
                  className="w-full bg-[#E50914] hover:bg-[#E50914]/90 text-white font-semibold h-12"
                  data-testid="go-login-after-reset-btn"
                >
                  Iniciar sesion
                </Button>
              </div>
            ) : (
              <form onSubmit={handleReset} className="space-y-4">
                <div>
                  <Label htmlFor="new-password" className="text-white">Nueva contrasena</Label>
                  <Input
                    id="new-password"
                    type="password"
                    placeholder="••••••••"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={6}
                    className="bg-white/5 border-white/10 focus:border-[#E50914]/50 text-white placeholder:text-white/30 h-12"
                    data-testid="new-password-input"
                  />
                </div>
                <div>
                  <Label htmlFor="confirm-password" className="text-white">Confirmar contrasena</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={6}
                    className="bg-white/5 border-white/10 focus:border-[#E50914]/50 text-white placeholder:text-white/30 h-12"
                    data-testid="confirm-password-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#E50914] hover:bg-[#E50914]/90 text-white shadow-[0_0_20px_rgba(229,9,20,0.4)] hover:shadow-[0_0_30px_rgba(229,9,20,0.6)] transition-all duration-300 font-semibold h-12"
                  data-testid="reset-submit-btn"
                >
                  {loading ? "Actualizando..." : "Restablecer contrasena"}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ResetPassword;
